import { type DuplicateMatch, type FieldDef, layoutSections, type ObjectDef, type PageLayout, type RecordData, type RecordResult, recordName, writableFields } from '@crm/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { api, ApiError, qs } from '../lib/api';
import { useRecordTypes, useSession } from '../lib/session';
import { FieldInput } from './FieldInput';

interface Props {
  obj: ObjectDef;
  /** Existing record to edit; omit to create. */
  record?: RecordData;
  refs?: Record<string, string>;
  /** Pre-filled values for a new record (e.g. accountId from a related list). */
  defaults?: Record<string, unknown>;
  onClose: () => void;
  onSaved: (r: RecordResult) => void;
  /** API path for the object's records (custom objects use /api/co/<name>). */
  endpoint?: string;
  /** Layout to use instead of the object's (custom objects pass theirs). */
  layout?: PageLayout | null;
}

const PERSON_FIELDS = ['personFirstName', 'personLastName', 'personEmail', 'personMobilePhone'];

export function RecordForm({ obj, record, refs = {}, defaults = {}, onClose, onSaved, endpoint, layout: layoutOverride }: Props) {
  const { user, layouts, features, permissions } = useSession();
  const qc = useQueryClient();
  const isNew = !record;
  const base = endpoint ?? `/api/records/${obj.name}`;
  const standard = !endpoint;
  const recordTypes = useRecordTypes(obj.name).filter((r) => r.isActive || r.id === record?.recordTypeId);
  const hasRecordTypes = obj.fields.some((f) => f.name === 'recordTypeId');
  const initial = (): Record<string, unknown> =>
    isNew
      ? {
          ownerId: user.id,
          ...(obj.name === 'lead' ? { status: 'new' } : {}),
          ...(hasRecordTypes ? { recordTypeId: recordTypes.find((r) => r.isDefault)?.id ?? null } : {}),
          ...defaults,
        }
      : { ...record };
  const [values, setValues] = useState<Record<string, unknown>>(initial);
  // The record as the form opened it: what changed is measured against this, and saved with its version. A colleague's
  // save meanwhile then ends in the usual "changed by someone else" message instead of being overwritten.
  const opened = useRef(record);
  /** "Save & New": after saving, the form clears for the next record and says what was created. */
  const [again, setAgain] = useState(false);
  const [created, setCreated] = useState<{ id: string; name: string } | null>(null);
  const [formKey, setFormKey] = useState(0);
  const recordType = recordTypes.find((r) => r.id === values.recordTypeId) ?? null;
  const personMode = obj.name === 'account' && features.personAccounts;
  const isPerson = personMode && values.isPersonAccount === true;

  const fields = writableFields(obj);
  /** Which fields the form shows for this record (person accounts swap the name for first/last name). */
  const visible = (f: FieldDef) => {
    // Fields pre-sales fills come from My Desk: not in the sales form.
    if (f.fillBy === 'presales' && !permissions.system.presalesWork && !permissions.system.manageSetup) return false;
    if (!personMode) return !f.personAccount;
    if (f.name === 'isPersonAccount') return false; // chosen with the switch above the form
    if (PERSON_FIELDS.includes(f.name)) return isPerson;
    if (f.name === 'name') return !isPerson;
    return true;
  };
  // The page layout decides which fields the form shows and in which sections; owner, stage, record type
  // and (for person accounts) the person's names are always shown.
  const sections = (() => {
    const layout = layoutOverride !== undefined ? layoutOverride ?? undefined : recordType?.layout ?? (layouts as Record<string, PageLayout | undefined>)[obj.name];
    const writable = new Set(fields.filter(visible).map((f) => f.name));
    const out = layoutSections(obj, layout).map((s) => ({ ...s, fields: s.fields.filter((f) => writable.has(f.name)) })).filter((s) => s.fields.length);
    const placed = new Set(out.flatMap((s) => s.fields.map((f) => f.name)));
    const always = fields.filter((f) => visible(f) && !placed.has(f.name)
      && (f.name === 'ownerId' || f.type === 'stage' || f.name === 'recordTypeId' || PERSON_FIELDS.includes(f.name) || (f.required && isNew)));
    if (always.length) (out[0] ?? out[out.push({ title: 'Details', columns: 2, fields: [] }) - 1]!).fields.unshift(...always);
    return out;
  })();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [duplicates, setDuplicates] = useState<DuplicateMatch[] | null>(null);
  const assignable = useQuery({
    queryKey: ['meta', 'assignment-active'],
    enabled: standard && isNew && obj.name === 'lead',
    queryFn: () => api<{ active: boolean }>('/api/meta/assignment-active').then((r) => r.active),
  });
  const [useAssignment, setUseAssignment] = useState(true);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  const save = useMutation({
    mutationFn: (opts: { allowDuplicates?: boolean } = {}) => {
      const sent = fields.filter((f) => visible(f) || (f.name === 'isPersonAccount' && personMode && isNew));
      // On edit, send only changed fields so concurrent edits to other fields aren't overwritten.
      const body = isNew
        ? Object.fromEntries(sent.map((f) => [f.name, values[f.name]]).filter(([, v]) => v !== undefined && v !== ''))
        : Object.fromEntries(sent.filter((f) => values[f.name] !== opened.current![f.name]).map((f) => [f.name, values[f.name] ?? null]));
      const q = standard ? qs({ assign: assignable.data && useAssignment ? 'true' : undefined, allowDuplicates: opts.allowDuplicates ? 'true' : undefined }) : '';
      return isNew
        ? api<RecordResult>(`${base}${q}`, { method: 'POST', body })
        : api<RecordResult>(`${base}/${record!.id}${standard ? qs({ allowDuplicates: opts.allowDuplicates ? 'true' : undefined }) : ''}`, { method: 'PATCH', body, headers: { 'if-match': String(opened.current!.version) } });
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['records', obj.name] });
      qc.setQueryData(['record', obj.name, r.record.id], r);
      if (again && isNew) {
        setCreated({ id: r.record.id, name: recordName(obj, r.record) || `New ${obj.label.toLowerCase()}` });
        setValues(initial());
        setErrors({});
        setDuplicates(null);
        setFormKey((k) => k + 1);
        dialog.current?.querySelector('.modal-scroll')?.scrollTo({ top: 0 });
        return;
      }
      onSaved(r);
    },
    onError: (err) => {
      if (err instanceof ApiError && err.code === 'duplicates') {
        setDuplicates(err.details as DuplicateMatch[]);
        setFormError(null);
        return;
      }
      if (err instanceof ApiError) {
        setErrors(err.fieldErrors());
        setFormError(err.message);
      } else setFormError('Could not save. Check your connection and try again.');
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    const missing: Record<string, string> = {};
    for (const f of fields) {
      if (!visible(f)) continue;
      if (f.required && f.type !== 'stage' && (values[f.name] === undefined || values[f.name] === null || values[f.name] === '')) missing[f.name] = 'Required';
    }
    if (isPerson && !values.personLastName) missing.personLastName = 'Required';
    setErrors(missing);
    if (Object.keys(missing).length) {
      setFormError('Fill in the required fields.');
      return;
    }
    setFormError(null);
    setDuplicates(null);
    save.mutate({});
  }

  const setValue = (f: FieldDef, v: unknown) => setValues((cur) => ({
    ...cur,
    [f.name]: v,
    // A new pipeline (or a record type with another pipeline) resets the stage: the server starts at its first open stage.
    ...(f.name === 'pipelineId' && v !== cur.pipelineId ? { stageId: record?.stageId ?? undefined } : {}),
    ...(f.name === 'recordTypeId' && obj.name === 'opportunity' && v !== cur.recordTypeId ? { stageId: undefined, pipelineId: undefined } : {}),
  }));
  const context = { ...values, pipelineId: values.pipelineId ?? recordType?.pipelineId ?? undefined };

  return (
    <dialog ref={dialog} className="modal record-modal" onClose={onClose} aria-labelledby="rf-title">
      <form onSubmit={onSubmit} noValidate>
        <header className="record-modal-head">
          <h2 id="rf-title">{isNew ? `New ${obj.label}` : `Edit ${recordName(obj, record!) || obj.label}`}</h2>
          <button type="button" className="icon" aria-label="Close" onClick={() => dialog.current?.close()}>×</button>
        </header>
        <div className="modal-scroll" key={formKey}>
        {created && (
          <p className="toast-inline" role="status">
            <svg viewBox="0 0 20 20" aria-hidden="true"><circle cx="10" cy="10" r="9" fill="currentColor" /><path d="m6 10.2 2.7 2.7L14 7.6" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
            {obj.label} “<Link to={`/o/${obj.name}/${created.id}`} target="_blank">{created.name}</Link>” was created. Enter the next one.
          </p>
        )}
        <p className="req-note"><span className="req">*</span> = Required information</p>
        {personMode && isNew && (
          <div className="seg" role="group" aria-label="Kind of account">
            <button type="button" aria-pressed={!isPerson} onClick={() => setValues((v) => ({ ...v, isPersonAccount: false }))}>Business</button>
            <button type="button" aria-pressed={isPerson} onClick={() => setValues((v) => ({ ...v, isPersonAccount: true }))}>Person (individual)</button>
          </div>
        )}
        {sections.map((s) => (
          <fieldset key={s.title} className="form-section">
            <legend>{sections.length > 1 || s.title !== 'Details' ? s.title : 'About'}</legend>
            <div className={s.columns === 1 ? 'form-grid one-col' : 'form-grid'}>
              {s.fields.map((f) => (
                <FieldInput
                  key={f.name}
                  field={f}
                  value={values[f.name]}
                  refLabel={typeof values[f.name] === 'string' ? refs[values[f.name] as string] : undefined}
                  error={errors[f.name]}
                  context={context}
                  objectName={obj.name}
                  recordType={recordType}
                  onChange={(v) => setValue(f, v)}
                />
              ))}
            </div>
          </fieldset>
        ))}
        {formError && <p className="form-error" role="alert">{formError}</p>}
        {duplicates && (
          <div className="dupes" role="alert">
            <p><b>Possible duplicate.</b> These existing records look the same:</p>
            <ul>
              {duplicates.map((d) => (
                <li key={`${d.objectName}:${d.id}`}><Link to={`/o/${d.objectName}/${d.id}`} target="_blank">{d.name || '(no name)'}</Link> <span className="muted small">{d.objectName} · {d.reason}</span></li>
              ))}
            </ul>
            <button type="button" className="btn small" disabled={save.isPending} onClick={() => save.mutate({ allowDuplicates: true })}>Save anyway</button>
          </div>
        )}
        </div>
        <footer className="record-modal-foot">
          {isNew && assignable.data && (
            <label className="radio assign"><input type="checkbox" checked={useAssignment} onChange={(e) => setUseAssignment(e.target.checked)} /> Assign using the active assignment rule</label>
          )}
          <button type="button" className="btn pill-btn" onClick={() => dialog.current?.close()}>Cancel</button>
          {isNew && <button type="submit" className="btn pill-btn" disabled={save.isPending} onClick={() => setAgain(true)}>Save &amp; New</button>}
          <button type="submit" className="btn primary pill-btn" disabled={save.isPending} onClick={() => setAgain(false)}>{save.isPending ? 'Saving…' : 'Save'}</button>
        </footer>
      </form>
    </dialog>
  );
}
